import React, {useEffect, useMemo} from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import {AddLarge, WarningAlt} from '@carbon/icons-react';
import {
  createFilterStore,
  FilterStoreContext,
  useFilterStore,
} from '../AccountGrid/filterStore';
import {AccountViewContext} from '../AccountGrid/AccountViewContext';
import {FilterItemList} from '../AccountGrid/FilterPopoverComponents/FilterItemList';
import {ToggleFilterControl} from '../AccountGrid/FilterPopoverComponents/ToggleFilterControl';
import {filterItemsToConditions} from '../AccountGrid/filterTypes';
import {
  createAccountFilter,
  AccountView,
} from '../../shared/filter/accountFilter';
import {ScoringRunProvider} from '../../context/ScoringRunContext';
import {ScoredAccountsResponse} from '../../shared/api/responses';
import {useSaveTagMutation} from '../../hooks/api/tags';
import {TagName} from '../../shared/validation';
import {DynamicTagResponse} from '../../shared/tags';
import {CompanyLogo} from '../Icons/CompanyLogo';
import {
  SegmentedRadioButtonGroup,
  SegmentedRadioButton,
} from '../segmentedRadioButtons';
import {useDynamicListFilterOptions} from './useDynamicListFilterOptions';
import {useDynamicListPreview} from './useDynamicListPreview';
import {entries} from '../../shared/util';

const filterStore = createFilterStore();

export const DynamicListEditorModal = ({
  tag,
  isOpen,
  onClose,
}: {
  tag?: DynamicTagResponse;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
      <ModalOverlay />
      <FilterStoreContext.Provider value={filterStore}>
        <DynamicListEditorContent tag={tag} onClose={onClose} />
      </FilterStoreContext.Provider>
    </Modal>
  );
};

const viewLabels: Record<Exclude<AccountView, 'recommended'>, string> = {
  all: 'All',
  saved: 'Saved',
  crm: 'CRM',
  imported: 'Imported',
  intent: 'Intent',
};

type FormData = {
  tagName: string;
};

const DynamicListEditorContent = ({
  tag,
  onClose,
}: {
  tag?: DynamicTagResponse;
  onClose: () => void;
}) => {
  const {
    filterItems,
    filterOperator,
    excluded,
    addEmptyFilterCondition,
    addEmptyFilterGroup,
    resetFilters,
    setExcluded,
    setFilterState,
  } = useFilterStore();

  const view = tag?.filter.view ?? 'all';
  const saveTag = useSaveTagMutation();
  const availableOptions = useDynamicListFilterOptions();

  // Populate filter state when editing an existing dynamic tag
  useEffect(() => {
    setFilterState(tag?.filter ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    formState: {errors, isValid},
    handleSubmit,
    register,
    reset: resetForm,
    watch,
  } = useForm<FormData>({
    defaultValues: {tagName: ''},
    values: {tagName: tag?.name ?? ''},
    mode: 'onChange',
  });

  const tagName = watch('tagName');
  const characterCount = tagName.trim().length;

  // Build the AccountFilter from current filter state
  const accountFilter = useMemo(() => {
    const {items, operator} = filterItemsToConditions(
      filterItems,
      filterOperator
    );

    return createAccountFilter(items, {
      operator,
      excluded,
      view,
    });
  }, [filterItems, filterOperator, excluded, view]);

  const {
    data: preview,
    isLoading: previewIsLoading,
    isFetching: previewIsPending,
  } = useDynamicListPreview(accountFilter);
  const scoringRun = preview?.scoringRun;

  const hasConditions = (accountFilter.conditions?.items.length ?? 0) > 0;
  const canSave = isValid && hasConditions;

  const handleClose = () => {
    resetFilters();
    resetForm();
    onClose();
  };

  const handleSave = (data: FormData) => {
    if (saveTag.isPending || !accountFilter) {
      return;
    }

    saveTag.mutate(
      {
        type: 'dynamic',
        name: data.tagName,
        filter: accountFilter,
        id: tag?._id,
      },
      {
        onSuccess: handleClose,
      }
    );
  };

  const saveDisabledReason = !hasConditions
    ? 'Add at least one filter condition'
    : !isValid
      ? 'Enter a valid list name'
      : undefined;

  return (
    <ModalContent
      as="form"
      onSubmit={handleSubmit(handleSave)}
      rounded="xl"
      h="80vh"
      minH="500px"
    >
      <ModalCloseButton />

      {/* Main area: left editor + right preview */}
      <Flex flex="1" overflow="hidden" minH="400px">
        {/* Left column: Filter editor */}
        <Flex direction="column" flex="1" overflow="hidden">
          <Box fontWeight="normal" fontSize="xl" px="6" pb="1" pt="6">
            {tag ? 'Edit Dynamic List' : 'Create Dynamic List'}
          </Box>

          <Flex direction="column" p="6" pb="3" gap="3">
            <FormControl isInvalid={!!errors.tagName}>
              <Box
                fontWeight="medium"
                fontSize="xs"
                mb="2"
                textTransform="uppercase"
              >
                List name
              </Box>
              <Input
                autoComplete="off"
                maxLength={TagName.maxLength}
                placeholder="eg. Enterprise SaaS Companies"
                {...register('tagName', {
                  minLength: TagName.minLength,
                  maxLength: TagName.maxLength,
                  pattern: TagName.pattern,
                  required: true,
                  setValueAs: (value) => value.trim(),
                })}
              />
              <Box fontSize="xs" color="kgray.300" mt="1">
                {characterCount}/{TagName.maxLength} characters
              </Box>
            </FormControl>

            <Divider />

            <SegmentedRadioButtonGroup
              value={view}
              size="sm"
              isDisabled
              alignSelf="end"
            >
              <>
                {entries(viewLabels).map(([key, label]) => (
                  <SegmentedRadioButton
                    key={key}
                    value={key}
                    isDisabled
                    sx={
                      key === view
                        ? {
                            '&[data-checked]': {
                              bg: 'kblue.50',
                              borderColor: 'kblue.200',
                              color: 'kblue.400',
                            },
                          }
                        : {}
                    }
                    _hover={{bg: 'none'}}
                  >
                    {label}
                  </SegmentedRadioButton>
                ))}
              </>
            </SegmentedRadioButtonGroup>

            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="500" fontSize="sm">
                Include accounts where...
              </Text>
              <ToggleFilterControl
                value={excluded ? 'excluded' : 'active'}
                onChange={(value) => setExcluded(value === 'excluded')}
                options={[
                  {value: 'active', label: 'Active'},
                  {value: 'excluded', label: 'Excluded'},
                ]}
              />
            </Flex>
          </Flex>

          <Box flex="1" overflowY="auto" px="6" fontSize="14px">
            <AccountViewContext.Provider value={view}>
              {previewIsLoading ? (
                <Spinner />
              ) : scoringRun ? (
                <ScoringRunProvider scoringRun={scoringRun}>
                  <FilterItemList
                    availableOptions={availableOptions}
                    staticTagsOnly
                  />
                </ScoringRunProvider>
              ) : (
                <ScoringRunNotFoundEmptyState />
              )}
            </AccountViewContext.Provider>
          </Box>

          <Flex
            px="6"
            py="3"
            borderTop="1px"
            borderColor="kgray.200"
            gap="2"
            flexShrink={0}
          >
            <Button
              size="sm"
              variant="ghost"
              onClick={addEmptyFilterCondition}
              leftIcon={<AddLarge />}
            >
              Add filter
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={addEmptyFilterGroup}
              leftIcon={<AddLarge />}
            >
              Add filter group
            </Button>
            <Button size="sm" variant="ghost" onClick={resetFilters} ml="auto">
              Clear filters
            </Button>
          </Flex>
        </Flex>

        {/* Right column: Preview */}
        <PreviewPanel
          hasConditions={hasConditions}
          data={preview}
          isLoading={previewIsPending}
        />
      </Flex>

      <ModalFooter
        justifyContent="start"
        gap="4"
        borderTop="1px"
        borderColor="kgray.200"
      >
        <Tooltip
          label={saveDisabledReason}
          shouldWrapChildren
          isDisabled={canSave}
        >
          <Button
            colorScheme="kbuttonblue"
            isDisabled={!canSave}
            isLoading={saveTag.isPending}
            type="submit"
          >
            Save
          </Button>
        </Tooltip>
        <Button color="kgray.300" onClick={handleClose} variant="ghost">
          Cancel
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ScoringRunNotFoundEmptyState = () => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    py="12"
    gap="4"
    minH="200px"
  >
    <Box color="korange.400">
      <WarningAlt size={48} />
    </Box>
    <Text fontWeight="500" fontSize="md">
      Scoring run not found
    </Text>
    <Text fontSize="sm" color="kgray.300" textAlign="center" maxW="320px">
      Dynamic lists require an active scoring run to filter accounts. Complete a
      market run or scoring cycle first, then return here to build your list.
    </Text>
  </Flex>
);

const PreviewPanel = ({
  hasConditions,
  data,
  isLoading,
}: {
  hasConditions: boolean;
  data: ScoredAccountsResponse | undefined;
  isLoading: boolean;
}) => {
  const accounts = data?.accounts ?? [];
  const totalLength = data?.totalLength ?? 0;
  const showResults = hasConditions && !isLoading;

  return (
    <Flex
      direction="column"
      w="350px"
      minW="350px"
      bg="kgray.100"
      borderLeft="1px"
      borderColor="kgray.200"
      overflow="hidden"
      borderTopRightRadius="xl"
    >
      <Flex direction="column" px="6" pt="8" pb="5" flexShrink={0}>
        <Text
          fontWeight="500"
          fontSize="xs"
          textTransform="uppercase"
          color="kgray.300"
        >
          Preview List
        </Text>
        {showResults && (
          <Text fontSize="xl" fontWeight="500" mt="2">
            {totalLength.toLocaleString()} accounts
          </Text>
        )}
      </Flex>

      <Flex direction="column" flex="1" overflowY="auto" px="6" pb="4" gap="2">
        {!hasConditions && (
          <Flex flex="1" align="center" justify="center">
            <Text fontSize="sm" color="kgray.300" textAlign="center">
              Add filters to preview matching accounts
            </Text>
          </Flex>
        )}
        {hasConditions && isLoading && (
          <Flex justify="center" py="4">
            <Spinner size="sm" color="kgray.300" />
          </Flex>
        )}
        {showResults && accounts.length === 0 && (
          <Text py="4" fontSize="sm" color="kgray.300">
            No matching accounts
          </Text>
        )}
        {showResults &&
          accounts.map((account) => (
            <Flex
              key={account.accountId.toString()}
              bg="white"
              border="1px"
              borderColor="kgray.200"
              borderRadius="lg"
              px="4"
              h="56px"
              minH="56px"
              gap="3"
              alignItems="center"
            >
              <Box borderRadius="md" overflow="hidden" flexShrink={0}>
                <CompanyLogo
                  domain={account.accountDomain}
                  name={account.fields.name ?? account.accountDomain}
                  boxSize="32px"
                />
              </Box>
              <Text fontSize="md" noOfLines={1} flexShrink={1} minW="0">
                {account.fields.name ?? account.accountDomain}
              </Text>
              <Text
                fontSize="sm"
                color="kgray.300"
                noOfLines={1}
                flexShrink={0}
              >
                {account.accountDomain}
              </Text>
            </Flex>
          ))}
      </Flex>
    </Flex>
  );
};
